import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Flex,
  Heading,
  IconChevronDown,
  IconChevronUp,
  Text,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation';
import * as S from './styles';

import MarketPreview from './MarketPreview';
import FilterTray from 'app/shared/components/FilterTray';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';

import type { ClickstreamEvent } from '@zg-rentals/analytics';
import type { State } from 'app/shared/types/State';
import type { Market } from 'app/shared/types/Market';
import type { GaEventObjectType } from 'app/shared/types/GaEventObjectType';

interface MarketPreviewsProps {
  markets: Array<Market>;
  heading: string;
  subject: string;
  copyText?: string;
  useFilters?: boolean;
  isSmallScreen: boolean;
  isMobile: boolean;
  gaEvent: GaEventObjectType;
  clickstreamEvent?: ClickstreamEvent;
}

const MarketPreviews: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketPreviewsProps>>> = (
  props,
) => {
  const { heading, subject, copyText, useFilters = false, isSmallScreen, isMobile, gaEvent, clickstreamEvent } = props;
  const dispatch = useDispatch();
  const filter = useSelector((state: State) => state.filter);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const expand = useCallback(() => setIsExpanded(!isExpanded), [isExpanded, setIsExpanded]);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);

  const markets = props.markets.filter((market) => market.medianRent !== 0); // dont include when theres no data
  const copyTextAndFilter = (
    <>
      {copyText ? (
        <Text as="p" fontType="bodySmall" fontColor="textMedium" marginBottom="md">
          {copyText}
        </Text>
      ) : null}
      {useFilters ? (
        <FilterTray
          bedrooms={bedrooms}
          setBedrooms={setBedrooms}
          propertyTypes={propertyTypes}
          setPropertyTypes={setPropertyTypes}
          loading={loading}
          setLoading={setLoading}
          isMobile={isMobile}
          style={isSmallScreen ? {} : { marginRight: 60 }}
        />
      ) : null}
    </>
  );
  return (
    <Card
      cardType="secondary"
      padding="md"
      paddingBottom="md"
      paddingX={isSmallScreen ? undefined : 'md'}
      marginTop="md"
    >
      <Heading level="4" as="h2" fontFamily="serif">
        {heading}
      </Heading>
      {isSmallScreen ? (
        copyTextAndFilter
      ) : (
        <Flex display="flex" flexDirection="row" justifyContent="space-between" flexWrap="wrap">
          {copyTextAndFilter}
        </Flex>
      )}
      <S.MarketPreviewContainer
        display="grid"
        // @ts-ignore
        gap={isSmallScreen ? 'xs' : 'lg'}
        marginBottom="lg"
        // the filters act as a spacer, so need to add margin when the filters are not present
        marginTop={useFilters ? null : 'md'}
        loading={loading}
        isSmallScreen={isSmallScreen}
      >
        {markets.map((market, i) => {
          // only show first 4 top markets on mobile
          if (isSmallScreen && i > 3 && !isExpanded) {
            return (
              <VisuallyHidden key={`${market.name}-hidden`}>
                <MarketPreview
                  {...market}
                  isSmallScreen={isSmallScreen}
                  gaEvent={gaEvent}
                  clickstreamEvent={clickstreamEvent}
                />
              </VisuallyHidden>
            );
          }
          return (
            <MarketPreview
              {...market}
              isSmallScreen={isSmallScreen}
              key={market.name}
              gaEvent={gaEvent}
              clickstreamEvent={clickstreamEvent}
            />
          );
        })}
      </S.MarketPreviewContainer>
      {markets.length > 4 && isSmallScreen && (
        <TextButton onClick={expand}>
          {isExpanded ? (
            <>
              See less {subject}
              <IconChevronUp />
            </>
          ) : (
            <>
              See more {subject}
              <IconChevronDown />
            </>
          )}
        </TextButton>
      )}
    </Card>
  );
};

export default MarketPreviews;
